// src/components/EditChurchDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Grid,
  Typography,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  updateEglise,
  uploadEgliseLogo,
  deleteEgliseLogo,
  uploadEgliseCover,
  deleteEgliseCover,
} from '../services/egliseService';
import { Eglise, UpdateEgliseRequest } from '../types/church';
import { ImageUpload } from './common';

interface EditChurchDialogProps {
  open: boolean;
  onClose: () => void;
  church: Eglise | null;
  onSuccess: () => void;
}

const EditChurchDialog: React.FC<EditChurchDialogProps> = ({
  open,
  onClose,
  church,
  onSuccess,
}) => {
  const { t } = useTranslation();

  // Form state
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [openingHours, setOpeningHours] = useState('');
  const [capacity, setCapacity] = useState<number | ''>('');
  const [foundedYear, setFoundedYear] = useState<number | ''>('');
  const [denomination, setDenomination] = useState('');

  // Image state (managed separately from form, direct upload)
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Initialize form when church changes
  useEffect(() => {
    if (church) {
      setNom(church.nom || '');
      setDescription(church.description || '');
      setAddress(church.address || '');
      setPhone(church.phone || '');
      setEmail(church.email || '');
      setWebsite(church.website || '');
      setOpeningHours(church.opening_hours || '');
      setCapacity(church.capacity || '');
      setFoundedYear(church.founded_year || '');
      setDenomination(church.denomination || '');
      // Initialize image URLs
      setLogoUrl(church.logo_url || null);
      setCoverImageUrl(church.cover_image_url || null);
    }
  }, [church]);

  const handleSubmit = async () => {
    if (!church) return;
    setError(null);

    // Validation
    if (!nom.trim()) {
      setError(t('admin.church.errors.nameRequired'));
      return;
    }

    if (nom.trim().length < 2) {
      setError(t('admin.church.errors.nameTooShort'));
      return;
    }

    if (nom.trim().length > 200) {
      setError(t('admin.church.errors.nameTooLong'));
      return;
    }

    try {
      setLoading(true);
      const payload: UpdateEgliseRequest = {};

      // Only include changed fields
      if (nom.trim() !== church.nom) payload.nom = nom.trim();
      if (description.trim() !== (church.description || '')) payload.description = description.trim();
      if (address.trim() !== (church.address || '')) payload.address = address.trim();
      if (phone.trim() !== (church.phone || '')) payload.phone = phone.trim();
      if (email.trim() !== (church.email || '')) payload.email = email.trim();
      if (website.trim() !== (church.website || '')) payload.website = website.trim();
      if (openingHours.trim() !== (church.opening_hours || '')) payload.opening_hours = openingHours.trim();
      if (capacity !== '' && capacity !== church.capacity) payload.capacity = capacity as number;
      if (foundedYear !== '' && foundedYear !== church.founded_year) payload.founded_year = foundedYear as number;
      if (denomination.trim() !== (church.denomination || '')) payload.denomination = denomination.trim();

      // Only call API if there are changes
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }

      await updateEglise(church.id, payload);
      onSuccess();
    } catch (err: any) {
      console.error('Failed to update church:', err);
      setError(err.response?.data?.detail || t('admin.church.errorUpdating'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  // Logo upload/delete handlers
  const handleLogoUpload = async (file: File, onProgress: (progress: number) => void) => {
    if (!church) return;
    try {
      const result = await uploadEgliseLogo(church.id, file, onProgress);
      setLogoUrl(result.url);
    } catch (err: any) {
      console.error('Failed to upload logo:', err);
      throw new Error(err.response?.data?.detail || t('imageUpload.uploadError'));
    }
  };

  const handleLogoDelete = async () => {
    if (!church) return;
    try {
      await deleteEgliseLogo(church.id);
      setLogoUrl(null);
    } catch (err: any) {
      console.error('Failed to delete logo:', err);
      throw new Error(err.response?.data?.detail || t('imageUpload.deleteError'));
    }
  };

  // Cover image upload/delete handlers
  const handleCoverUpload = async (file: File, onProgress: (progress: number) => void) => {
    if (!church) return;
    try {
      const result = await uploadEgliseCover(church.id, file, onProgress);
      setCoverImageUrl(result.url);
    } catch (err: any) {
      console.error('Failed to upload cover:', err);
      throw new Error(err.response?.data?.detail || t('imageUpload.uploadError'));
    }
  };

  const handleCoverDelete = async () => {
    if (!church) return;
    try {
      await deleteEgliseCover(church.id);
      setCoverImageUrl(null);
    } catch (err: any) {
      console.error('Failed to delete cover:', err);
      throw new Error(err.response?.data?.detail || t('imageUpload.deleteError'));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('admin.church.editTitle')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Images Section */}
          <Typography variant="subtitle1" fontWeight="bold">
            {t('admin.church.images.title')}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <ImageUpload
                currentImageUrl={logoUrl}
                onUpload={handleLogoUpload}
                onDelete={handleLogoDelete}
                maxSizeMB={5}
                label={t('admin.church.images.logo')}
                helperText={t('admin.church.images.logoHelper')}
                disabled={loading}
                shape="circle"
                width={150}
                height={150}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <ImageUpload
                currentImageUrl={coverImageUrl}
                onUpload={handleCoverUpload}
                onDelete={handleCoverDelete}
                maxSizeMB={10}
                label={t('admin.church.images.cover')}
                helperText={t('admin.church.images.coverHelper')}
                disabled={loading}
                shape="rectangle"
                width="100%"
                height={200}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Nom de l'église */}
          <TextField
            label={t('admin.church.form.name')}
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            fullWidth
            required
            disabled={loading}
          />

          {/* Description */}
          <TextField
            label={t('admin.church.form.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
            disabled={loading}
          />

          {/* Adresse */}
          <TextField
            label={t('admin.church.form.address')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            fullWidth
            disabled={loading}
          />

          {/* Téléphone et Email */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('admin.church.form.phone')}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('admin.church.form.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                disabled={loading}
                type="email"
              />
            </Grid>
          </Grid>

          {/* Website */}
          <TextField
            label={t('admin.church.form.website')}
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            fullWidth
            disabled={loading}
            type="url"
            placeholder="https://..."
          />

          {/* Opening Hours et Denomination */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('admin.church.form.openingHours')}
                value={openingHours}
                onChange={(e) => setOpeningHours(e.target.value)}
                fullWidth
                disabled={loading}
                placeholder="Lun-Ven: 9h-18h"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('admin.church.form.denomination')}
                value={denomination}
                onChange={(e) => setDenomination(e.target.value)}
                fullWidth
                disabled={loading}
                placeholder="Orthodoxe"
              />
            </Grid>
          </Grid>

          {/* Capacity et Founded Year */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('admin.church.form.capacity')}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value ? parseInt(e.target.value) : '')}
                fullWidth
                disabled={loading}
                type="number"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('admin.church.form.foundedYear')}
                value={foundedYear}
                onChange={(e) => setFoundedYear(e.target.value ? parseInt(e.target.value) : '')}
                fullWidth
                disabled={loading}
                type="number"
                inputProps={{ min: 1, max: new Date().getFullYear() }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? t('admin.church.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditChurchDialog;
